/**
 * Firebase Authentication & Mock Fallback Service
 * Path: packages/web/src/services/firebase.ts
 */

import { initializeApp, getApps, type FirebaseApp } from 'firebase/app';
import {
  getAuth,
  signInWithPopup,
  GoogleAuthProvider,
  signOut as firebaseSignOut,
  onAuthStateChanged as firebaseOnAuthStateChanged,
  getIdTokenResult,
  type Auth,
  type User as FirebaseUser,
  type Unsubscribe,
} from 'firebase/auth';

export type UserRole = 'operator' | 'tenant-admin' | 'super-admin';

export interface AuthUserState {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  tenantId: string;
  role: UserRole;
  isMock?: boolean;
}

// Read Vite Environment Variables with fallback defaults
const firebaseConfig = {
  apiKey: (typeof import.meta !== 'undefined' && import.meta.env?.VITE_FIREBASE_API_KEY) || 'AIzaSyMockApiKeyGateflow2026',
  authDomain: (typeof import.meta !== 'undefined' && import.meta.env?.VITE_FIREBASE_AUTH_DOMAIN) || 'gateflow-pgwiz.firebaseapp.com',
  projectId: (typeof import.meta !== 'undefined' && import.meta.env?.VITE_FIREBASE_PROJECT_ID) || 'gateflow-pgwiz',
  storageBucket: (typeof import.meta !== 'undefined' && import.meta.env?.VITE_FIREBASE_STORAGE_BUCKET) || 'gateflow-pgwiz.appspot.com',
  messagingSenderId: (typeof import.meta !== 'undefined' && import.meta.env?.VITE_FIREBASE_MESSAGING_SENDER_ID) || '123456789012',
  appId: (typeof import.meta !== 'undefined' && import.meta.env?.VITE_FIREBASE_APP_ID) || '1:123456789012:web:mockappid123456',
};

// Check if mock mode is forced or running without real Firebase credentials
export const isMockAuthMode = (): boolean => {
  if (typeof window === 'undefined') return true;
  return (
    (typeof import.meta !== 'undefined' && import.meta.env?.VITE_MOCK_AUTH === 'true') ||
    !(typeof import.meta !== 'undefined' && import.meta.env?.VITE_FIREBASE_API_KEY) ||
    (typeof import.meta !== 'undefined' && import.meta.env?.VITE_FIREBASE_API_KEY?.startsWith('AIzaSyMock'))
  );
};

let app: FirebaseApp | null = null;
let auth: Auth | null = null;

export function getFirebaseApp(): FirebaseApp {
  if (!app) {
    if (getApps().length > 0) {
      app = getApps()[0]!;
    } else {
      app = initializeApp(firebaseConfig);
    }
  }
  return app;
}

export function getFirebaseAuth(): Auth {
  if (!auth) {
    auth = getAuth(getFirebaseApp());
  }
  return auth;
}

/**
 * Extract tenantId and role custom claims from Firebase User
 */
export async function extractUserClaims(user: FirebaseUser): Promise<{ tenantId: string; role: UserRole }> {
  try {
    const tokenResult = await getIdTokenResult(user, true);
    const claims = tokenResult.claims;
    const tenantId =
      (claims.tenantId as string) ||
      (claims.tenant_id as string) ||
      (claims.tenant as string) ||
      'tenant-001';
    const role = (claims.role as UserRole) || 'operator';
    return { tenantId, role };
  } catch (err) {
    console.warn('Failed to extract custom claims from token, using defaults', err);
    return { tenantId: 'tenant-001', role: 'operator' };
  }
}

/**
 * Headless & Local Development Mock Authentication
 * Generates an ID token in the format expected by API firebase-auth middleware:
 * `mock:${uid}:${tenantId}:${role}`
 */
export function loginAsMock(
  role: UserRole = 'tenant-admin',
  tenantId: string = 'tenant-001',
  email: string = 'admin@gateflow.local'
): { user: AuthUserState; idToken: string } {
  const uid = `mock_${role}_${tenantId}`;
  const mockToken = `mock:${uid}:${tenantId}:${role}`;

  const mockUser: AuthUserState = {
    uid,
    email,
    displayName: `${role.toUpperCase().replace('-', ' ')} (Dev Mock)`,
    photoURL: `https://ui-avatars.com/api/?name=${encodeURIComponent(role)}&background=0284c7&color=fff`,
    tenantId,
    role,
    isMock: true,
  };

  if (typeof localStorage !== 'undefined') {
    localStorage.setItem('gateflow_mock_session', JSON.stringify({ user: mockUser, idToken: mockToken }));
  }
  return { user: mockUser, idToken: mockToken };
}

/**
 * Sign in using Google OAuth Popup
 * Automatically falls back to mock login if in mock mode or if popup is rejected in test
 */
export async function signInWithGoogle(): Promise<{ user: AuthUserState; idToken: string }> {
  if (isMockAuthMode()) {
    return loginAsMock('tenant-admin', 'tenant-001', 'admin@gateflow.local');
  }

  try {
    const firebaseAuth = getFirebaseAuth();
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });

    const credential = await signInWithPopup(firebaseAuth, provider);
    const idToken = await credential.user.getIdToken(true);
    const { tenantId, role } = await extractUserClaims(credential.user);

    const userState: AuthUserState = {
      uid: credential.user.uid,
      email: credential.user.email,
      displayName: credential.user.displayName,
      photoURL: credential.user.photoURL,
      tenantId,
      role,
      isMock: false,
    };

    return { user: userState, idToken };
  } catch (err: unknown) {
    console.error('Google Sign-in popup error:', err);
    if (typeof import.meta !== 'undefined' && import.meta.env?.DEV) {
      console.warn('Falling back to dev mock user due to Google popup failure');
      return loginAsMock('tenant-admin', 'tenant-001', 'admin@gateflow.local');
    }
    throw err;
  }
}

/**
 * Sign out of current session
 */
export async function signOutAuth(): Promise<void> {
  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem('gateflow_mock_session');
    localStorage.removeItem('gateflow_token');
    localStorage.removeItem('gateflow_tenant_id');
    localStorage.removeItem('gateflow_role');
  }
  if (!isMockAuthMode()) {
    try {
      const firebaseAuth = getFirebaseAuth();
      await firebaseSignOut(firebaseAuth);
    } catch (e) {
      console.warn('Firebase signOut error', e);
    }
  }
}

/**
 * Subscribe to Auth State Changes
 */
export function onAuthStateChanged(
  callback: (user: AuthUserState | null, idToken: string | null) => void
): Unsubscribe {
  if (isMockAuthMode()) {
    if (typeof localStorage !== 'undefined') {
      const raw = localStorage.getItem('gateflow_mock_session');
      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          callback(parsed.user, parsed.idToken);
        } catch {
          callback(null, null);
        }
      } else {
        callback(null, null);
      }
    } else {
      callback(null, null);
    }
    return () => {};
  }

  const firebaseAuth = getFirebaseAuth();
  return firebaseOnAuthStateChanged(firebaseAuth, async (fbUser) => {
    if (fbUser) {
      try {
        const idToken = await fbUser.getIdToken();
        const { tenantId, role } = await extractUserClaims(fbUser);
        const userState: AuthUserState = {
          uid: fbUser.uid,
          email: fbUser.email,
          displayName: fbUser.displayName,
          photoURL: fbUser.photoURL,
          tenantId,
          role,
          isMock: false,
        };
        callback(userState, idToken);
      } catch (err) {
        console.error('Error processing auth state change', err);
        callback(null, null);
      }
    } else {
      callback(null, null);
    }
  });
}
